import { AccordionItem, Input, Status } from '@mm/components';
import { usePromoterCRUDActions } from '@mm/state/actions/PromoterCRUDActions';
import { Switch } from 'antd';
import shopifyIcon from 'assets/shopify-icon.png';
import { Button } from 'packages/components';
import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';

import { CopyOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useSuperpixelConfigurationCRUDActions } from '@mm/state/actions/SuperpixelConfigurationCRUDActions';
import { showSlideoutMenu } from '@mm/state/actions/viewActions';
import { PromoterTypes } from '@mm/state/model/PromoterModel';
import {
  activeChatbotIdSelector,
  activeFacebookPageIdSelector,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import {
  errorToast,
  successToast,
} from '../../../../../../../../../common/helpers/toast';
import {
  useCustomPromoterJSCodes,
  usePromoterJSCode,
} from '../../../../../../../startBotPromoters/common/use-promoter-js-code';
import { colors } from '../../../../../../styles/colors';
import css from './SuperpixelSection.module.css';

function normalizeUrl(url) {
  try {
    // Trim leading/trailing spaces first
    let trimmedUrl = url.trim();

    // Ensure the URL has a protocol for parsing
    if (!/^https?:\/\//i.test(trimmedUrl)) {
      trimmedUrl = `http://${trimmedUrl}`;
    }

    // Use the URL constructor to parse the URL
    const parsedUrl = new URL(trimmedUrl);

    // Remove the 'www.' prefix if it exists
    return parsedUrl.hostname.replace(/^www\./, '');
  } catch (error) {
    // Fallback for invalid URLs
    return url.trim().replace(/^www\./, '');
  }
}

function extractHost(urlOrHost: string): string | null {
  try {
    const url = urlOrHost.includes('://') ? urlOrHost : `http://${urlOrHost}`;
    const { host } = new URL(url);
    if (host.split('.').filter(Boolean).length === 1) {
      return null;
    }
    return host;
  } catch (e) {
    return null;
  }
}

export const SuperpixelSection = ({
  className,
  withButton,
  styles,
  onBeforeTitle,
  caretStyleIcon = true,
}: {
  className?: string;
  withButton?: boolean;
  styles?: {
    accordion?: string;
    expandSection?: string;
    instruction?: string;
  };
  onBeforeTitle?: ReactNode;
  caretStyleIcon?: boolean;
}) => {
  const dispatch = useDispatch();
  const { fetch, update } = useSuperpixelConfigurationCRUDActions();
  const activeFbPageId = useSelector(activeFacebookPageIdSelector);
  const botId = useSelector(activeChatbotIdSelector);
  const configuration = useSelector(
    (state: StoreState) => state.superpixelConfiguration.configuration,
  );
  const { retrieveAll } = usePromoterCRUDActions();
  const [promoterId, setPromoterId] = useState<number | undefined>();

  const retrieveGetemailPromoters = async (botId: number) => {
    const res = await retrieveAll(botId, 'getemails');
    if (res.length) {
      const firstPromoter = res?.[0];
      setPromoterId(firstPromoter.id);
    }
  };

  useEffect(() => {
    if (botId) {
      retrieveGetemailPromoters(botId);
    }
  }, [botId]);

  const promoter = useSelector((state: StoreState) =>
    state.promoter.entities.find(
      promoter => promoter.id?.toString() === promoterId?.toString(),
    ),
  );
  const promoterJSCode = usePromoterJSCode(promoter);
  const promoterJSCode2Map = useCustomPromoterJSCodes(promoter);

  const openShopifyInstallationGuide = () => {
    dispatch(
      showSlideoutMenu('customerChatInstallationGuide', {
        siteType: 'Shopify',
        promoterJSCode,
        promoterJSCode2: promoterJSCode2Map.Shopify,
        promoterType: PromoterTypes.GETEMAILS,
      }),
    );
  };
  const [domain, setDomain] = useState(configuration?.rootDomain);
  const activateDeactivate = (active: boolean) => {
    if (configuration) {
      update({ id: configuration.id, active });
    }
  };
  const updateDomain = () => {
    if (configuration) {
      update({ id: configuration.id, domain });
    }
  };
  const verify = async () => {
    if (configuration) {
      const updatedRecord = await update({ id: configuration.id });
      if (updatedRecord?.verified) {
        successToast('The DNS records are properly configured!', 'top-right');
      } else {
        errorToast('The DNS records are not configured yet.', 'top-right');
      }
    }
  };
  const validDomain = !!extractHost(domain || '');

  const txtRecords = useMemo(() => {
    if (configuration) {
      return [
        { title: 'Type', value: 'TXT' },
        { title: 'Value', value: configuration.dnsTxtValue },
        { title: 'Name', value: configuration.dnsTxtName },
        {
          title: 'TTL',
          value: configuration.dnsTxtTtl || 'Auto / Default / Lowest Number',
        },
        ...(configuration.dnsTxtNote
          ? [{ title: 'Note', value: configuration.dnsTxtNote }]
          : []),
      ];
    }
    return [];
  }, [configuration]);

  const cnameRecords = useMemo(() => {
    if (configuration) {
      return [
        { title: 'Type', value: 'CNAME' },
        { title: 'Name', value: configuration.dnsCnameName },
        { title: 'Value', value: configuration.dnsCnameValue },
        {
          title: 'TTL',
          value: configuration.dnsCnameTtl || 'Auto / Default / Lowest Number',
        },
        ...(configuration.dnsCnameNote
          ? [{ title: 'Note', value: configuration.dnsCnameNote }]
          : []),
      ];
    }
    return [];
  }, [configuration]);

  const hasDnsValues = configuration?.dnsCnameName && configuration?.dnsTxtName;

  useEffect(() => {
    if (activeFbPageId) {
      (async () => {
        const configuration = await fetch(activeFbPageId);
        if (configuration) {
          setDomain(configuration?.rootDomain);
        }
      })();
    }
  }, [activeFbPageId]);

  return (
    <AccordionItem
      className={classNames('integrations-item', styles?.accordion)}
      closeOnOutsideClick={false}
      caretStyleIcon={caretStyleIcon}
      canToggle={true}
      openOnMount={false}
      titleComponent={
        <div className={css.header}>
          <div className={css.accordionTitle}>
            {onBeforeTitle} Add DNS records for advanced tracking
          </div>
          {!!configuration?.domain && (
            <div className={css.headerLeft} onClick={e => e.stopPropagation()}>
              <Switch
                className={css.switch}
                checked={configuration?.active || false}
                onClick={active => activateDeactivate(active)}
                size="small"
              />
              <Status
                status={configuration?.verified ? 'Verified' : 'Unverified'}
                color={
                  configuration?.verified ? colors.polarGreen : colors.orange
                }
                className={classNames(css.dnsStatus)}
              />
            </div>
          )}
          <div className={css.headerRight}>
            {withButton && (
              <Button
                css={{ container: classNames(css.btn, css.createBtn) }}
                color="secondary-blue"
              >
                Set Up Advanced Tracking
              </Button>
            )}
          </div>
        </div>
      }
    >
      <div className={classNames(css.container, className)}>
        <div className={classNames(css.expandSection, styles?.expandSection)}>
          <div className={css.domainSetup}>
            <div className={css.domainHeading}>Enter your domain</div>
            <div className={css.domainData}>
              <Input
                placeholder="Enter your domain"
                css={{
                  container: css.domainInputContainer,
                  input: css.domainInput,
                }}
                value={domain}
                onChange={e => setDomain(e.target.value)}
              />
              <Button
                css={{ container: css.btn }}
                onClick={() => updateDomain()}
                disabled={
                  !validDomain ||
                  normalizeUrl(configuration?.rootDomain || '') ===
                    normalizeUrl(domain)
                }
              >
                {configuration?.rootDomain ? 'Change' : 'Set'}
              </Button>
            </div>
          </div>
          {!!hasDnsValues && (
            <>
              <div className={classNames(css.instruction, styles?.instruction)}>
                <span className={css.instructionIntro}>
                  Copy/Paste TXT and CNAME records into your DNS to certify
                  you’re a legitimate first-party.
                  <br />
                  Once you’ve added both records, verify them below and we will
                  check to make sure everything’s mapped correctly.
                </span>

                <div className={css.instructionTableContainer}>
                  <span className={css.instructionTitle}>DNS Record TXT</span>
                  <ul className={css.instructionTable}>
                    {txtRecords.map(v => (
                      <li className={css.instructionTableRow} key={v.title}>
                        <div className={css.instructionTableColumn}>
                          {v.title}
                        </div>
                        <div className={css.instructionTableColumn}>
                          {v.value}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className={css.instructionTableContainer}>
                  <span className={css.instructionTitle}>DNS Record CNAME</span>
                  <ul className={css.instructionTable}>
                    {cnameRecords.map(v => (
                      <li className={css.instructionTableRow} key={v.title}>
                        <div className={css.instructionTableColumn}>
                          {v.title}
                        </div>
                        <div className={css.instructionTableColumn}>
                          {v.value}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className={css.buttonContainer}>
                  <Button
                    disabled={configuration?.verified}
                    css={{ container: css.btn }}
                    onClick={() => verify()}
                  >
                    {configuration?.verified ? 'Verified' : 'Verify Records'}
                  </Button>
                  {!configuration?.verified && (
                    <div className={css.verificationText}>
                      <InfoCircleOutlined className={css.infoIcon} />
                      Verification may take up to 4 hours for DNS records to
                      finalize.
                    </div>
                  )}
                </div>
              </div>
              <div className={classNames(css.instruction, styles?.instruction)}>
                <div className={css.instructionShopifyHeader}>
                  <div className={css.instructionShopifyHeaderText}>
                    <figure className={css.shopifyIcon}>
                      <img src={shopifyIcon} alt="Shopify" />
                    </figure>
                    <span className={css.instructionTitle}>
                      Ensure both pixels are installed
                    </span>
                  </div>
                  <div className={css.instructionShopifyHeaderSubtext}>
                    Make sure that the second pixel is active and installed on
                    your “Custom events area”
                  </div>
                </div>
                <div className={css.codeContainer}>
                  <textarea
                    readOnly
                    className={classNames(css.codeBlock, {
                      [css.smallCodeBlock]: true,
                    })}
                    value={promoterJSCode2Map.Shopify}
                    onFocus={e => e.target.select()}
                  />
                  <div className={css.codeButtons}>
                    <Button
                      css={{ container: css.btn }}
                      onClick={openShopifyInstallationGuide}
                    >
                      See Installation Guide
                    </Button>
                    <CopyToClipboard text={promoterJSCode2Map.Shopify}>
                      <Button
                        leftIcon={<CopyOutlined />}
                        color="secondary-blue"
                        css={{ container: css.codeBlockCopyButton }}
                      >
                        Copy Code
                      </Button>
                    </CopyToClipboard>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </AccordionItem>
  );
};
